import { type FunctionComponent } from "react";
import { graphql, useFragment } from "react-relay";

import { AppFooter } from "scmp-app/components/app-footer";
import { PlusArchiveHeader } from "scmp-app/components/plus/plus-archive-header";
import { PlusPaywallSuspense } from "scmp-app/components/plus/plus-paywall-suspense";
import type { archivePlusArchiveQuery$key } from "scmp-app/queries/__generated__/archivePlusArchiveQuery.graphql";

import { PlusArchivePlaceholder } from "./placeholder";
import {
  Column,
  Container,
  StyledChinaFutureTechBriefings,
  StyledPlusArchiveBetweenTheLines,
  StyledPlusArchiveDailyPulse,
  StyledPlusArchiveFactSheet,
  StyledPlusArchiveInteractives,
  StyledPlusArchiveOpenQuestionsSeries,
  StyledPlusArchiveQuickDigest,
  SubWrapper,
  Wrapper,
} from "./styles";

type Props = {
  className?: string;
  reference: archivePlusArchiveQuery$key;
};

export const PlusArchive: FunctionComponent<Props> = ({ className, reference: reference_ }) => {
  const data = useFragment(
    graphql`
      fragment archivePlusArchiveQuery on Query
      @argumentDefinitions(
        dailyPulseTypeUuid: { type: "String!" }
        factSheetTypeUuid: { type: "String!" }
        quickDigestTypeUuid: { type: "String!" }
        openQuestionsSeriesTypeUuid: { type: "String!" }
        chinaFutureTechBriefingsTypeUuid: { type: "String!" }
        betweenTheLinesTypeUuid: { type: "String!" }
      ) {
        ...dailyPulsePlusArchiveQuery @arguments(dailyPulseTypeUuid: $dailyPulseTypeUuid)
        ...chinaFutureTechBriefingsPlusArchiveQuery
          @arguments(chinaFutureTechBriefingsTypeUuid: $chinaFutureTechBriefingsTypeUuid)
        ...factSheetPlusArchiveQuery @arguments(factSheetTypeUuid: $factSheetTypeUuid)
        ...quickDigestPlusArchiveQuery @arguments(quickDigestTypeUuid: $quickDigestTypeUuid)
        ...openQuestionsSeriesPlusArchiveQuery
          @arguments(openQuestionsSeriesTypeUuid: $openQuestionsSeriesTypeUuid)
        ...betweenTheLinesPlusArchiveQuery
          @arguments(betweenTheLinesTypeUuid: $betweenTheLinesTypeUuid)
        ...interactivesPlusArchiveQuery
      }
    `,
    reference_,
  );

  return (
    <Container className={className}>
      <PlusPaywallSuspense fallback={<PlusArchivePlaceholder />}>
        <Wrapper>
          <PlusArchiveHeader title="Plus Archive" />
          <SubWrapper>
            <Column>
              <StyledPlusArchiveFactSheet reference={data} />
              <StyledPlusArchiveQuickDigest reference={data} />
            </Column>
            <Column>
              <StyledPlusArchiveOpenQuestionsSeries reference={data} />
            </Column>
            <Column>
              <StyledPlusArchiveBetweenTheLines reference={data} />
              <StyledChinaFutureTechBriefings reference={data} />
            </Column>
            <Column>
              <StyledPlusArchiveDailyPulse reference={data} />
              <StyledPlusArchiveInteractives reference={data} />
            </Column>
          </SubWrapper>
        </Wrapper>
        <AppFooter variant="plus-without-app-link-row" />
      </PlusPaywallSuspense>
    </Container>
  );
};

PlusArchive.displayName = "PlusArchive";
